import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const TaxEstimatorCalculator = () => {
  const content = article.find((item) => item.id === 4);
  const [mode, setMode] = useState("trader");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    // Trade Details
    buyPrice: "50.00",
    sellPrice: "65.00",
    shares: "100",
    purchaseDate: "2023-01-15",
    saleDate: "2024-01-15",
    
    // Income Information
    filingStatus: "single",
    annualIncome: "75000",
    state: "CA",
    otherInvestmentIncome: "5000",
    
    // Tax Settings
    taxYear: "2024",
    stateTaxRate: "9.3",
    applyNII: true,
    applyAMT: false,
    
    // Dividend Information
    qualifiedDividends: "1000",
    nonQualifiedDividends: "500",
    
    // Wash Sale
    washSaleAdjustment: "0",
    
    // Carryover Losses
    capitalLossCarryover: "0"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Tax brackets for 2024 (federal)
  const taxBrackets = {
    single: [
      { min: 0, max: 11600, rate: 0.10 },
      { min: 11601, max: 47150, rate: 0.12 },
      { min: 47151, max: 100525, rate: 0.22 },
      { min: 100526, max: 191950, rate: 0.24 },
      { min: 191951, max: 243725, rate: 0.32 },
      { min: 243726, max: 609350, rate: 0.35 },
      { min: 609351, max: Infinity, rate: 0.37 }
    ],
    married: [
      { min: 0, max: 23200, rate: 0.10 },
      { min: 23201, max: 94300, rate: 0.12 },
      { min: 94301, max: 201050, rate: 0.22 },
      { min: 201051, max: 383900, rate: 0.24 },
      { min: 383901, max: 487450, rate: 0.32 },
      { min: 487451, max: 731200, rate: 0.35 },
      { min: 731201, max: Infinity, rate: 0.37 }
    ]
  };

  // Long-term capital gains brackets
  const ltcgBrackets = {
    single: [
      { min: 0, max: 47025, rate: 0.00 },
      { min: 47026, max: 518900, rate: 0.15 },
      { min: 518901, max: Infinity, rate: 0.20 }
    ],
    married: [
      { min: 0, max: 94050, rate: 0.00 },
      { min: 94051, max: 583750, rate: 0.15 },
      { min: 583751, max: Infinity, rate: 0.20 }
    ]
  };

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateTax();
    }
  }, [inputs, mode, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateHoldingPeriod = (purchaseDate, saleDate) => {
    const purchase = new Date(purchaseDate);
    const sale = new Date(saleDate);
    const diffTime = Math.abs(sale - purchase);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateTax = () => {
    const {
      buyPrice, sellPrice, shares, purchaseDate, saleDate,
      filingStatus, annualIncome, stateTaxRate, applyNII,
      qualifiedDividends, nonQualifiedDividends, washSaleAdjustment,
      capitalLossCarryover, otherInvestmentIncome
    } = inputs;

    if (!buyPrice || !sellPrice || !shares) return;

    // Basic calculations
    const bp = parseFloat(buyPrice);
    const sp = parseFloat(sellPrice);
    const sh = parseFloat(shares);
    const income = parseFloat(annualIncome);
    const stateRate = parseFloat(stateTaxRate) / 100;
    const qualifiedDiv = parseFloat(qualifiedDividends || 0);
    const nonQualifiedDiv = parseFloat(nonQualifiedDividends || 0);
    const washSale = parseFloat(washSaleAdjustment || 0);
    const carryoverLoss = parseFloat(capitalLossCarryover || 0);
    const otherIncome = parseFloat(otherInvestmentIncome || 0);

    const totalCost = bp * sh;
    const totalProceeds = sp * sh;
    const grossGain = totalProceeds - totalCost;
    const netGain = grossGain - washSale;

    // Determine holding period and tax type
    const holdingDays = calculateHoldingPeriod(purchaseDate, saleDate);
    const isLongTerm = holdingDays > 365;
    const gainType = isLongTerm ? "Long-term" : "Short-term";

    // Calculate capital gains tax
    let federalTax = 0;
    let stateTax = 0;
    let additionalTax = 0;

    if (netGain > 0) {
      // Apply carryover losses first
      let taxableGain = Math.max(0, netGain - carryoverLoss);

      if (isLongTerm) {
        federalTax = calculateLongTermCapitalGainsTax(taxableGain, income, filingStatus);
      } else {
        federalTax = calculateShortTermCapitalGainsTax(taxableGain, income, filingStatus);
      }

      // State tax
      stateTax = taxableGain * stateRate;

      // Net Investment Income Tax (3.8% for high earners)
      if (applyNII && income + taxableGain > (filingStatus === 'single' ? 200000 : 250000)) {
        const niiThreshold = filingStatus === 'single' ? 200000 : 250000;
        const niiTaxableAmount = Math.max(0, income + taxableGain - niiThreshold);
        additionalTax += niiTaxableAmount * 0.038;
      }
    }

    // Dividend taxes
    const qualifiedDivTax = calculateQualifiedDividendTax(qualifiedDiv, income, filingStatus);
    const nonQualifiedDivTax = calculateShortTermCapitalGainsTax(nonQualifiedDiv, income, filingStatus);

    const totalFederalTax = federalTax + qualifiedDivTax + nonQualifiedDivTax + additionalTax;
    const totalTax = totalFederalTax + stateTax;
    const afterTaxProfit = netGain - totalTax + qualifiedDiv + nonQualifiedDiv;

    const resultData = {
      // Basic trade info
      totalCost,
      totalProceeds,
      grossGain,
      netGain,
      holdingDays,
      gainType,
      isLongTerm,

      // Tax calculations
      federalTax,
      stateTax,
      additionalTax,
      qualifiedDividendTax: qualifiedDivTax,
      nonQualifiedDividendTax: nonQualifiedDivTax,
      totalFederalTax,
      totalTax,
      afterTaxProfit,

      // Breakdown
      effectiveTaxRate: totalTax / Math.max(netGain, 1) * 100,
      taxToGainRatio: totalTax / Math.max(netGain, 1),

      // Additional info
      carryoverLossUsed: Math.min(carryoverLoss, netGain),
      remainingCarryover: Math.max(0, carryoverLoss - netGain)
    };

    setResult(resultData);
  };

  const calculateLongTermCapitalGainsTax = (gain, income, filingStatus) => {
    const brackets = ltcgBrackets[filingStatus];
    let tax = 0;
    let remainingGain = gain;

    for (const bracket of brackets) {
      if (remainingGain <= 0) break;

      const bracketIncome = income > bracket.min ? Math.min(income, bracket.max) - bracket.min : 0;
      const taxableInBracket = Math.min(remainingGain, bracket.max - Math.max(income, bracket.min));

      if (taxableInBracket > 0) {
        tax += taxableInBracket * bracket.rate;
        remainingGain -= taxableInBracket;
      }
    }

    return tax;
  };

  const calculateShortTermCapitalGainsTax = (gain, income, filingStatus) => {
    const brackets = taxBrackets[filingStatus];
    let tax = 0;
    let remainingGain = gain;

    for (const bracket of brackets) {
      if (remainingGain <= 0) break;

      const bracketMin = Math.max(bracket.min, income);
      const bracketMax = bracket.max;
      
      if (bracketMin < bracketMax) {
        const taxableInBracket = Math.min(remainingGain, bracketMax - bracketMin);
        tax += taxableInBracket * bracket.rate;
        remainingGain -= taxableInBracket;
      }
    }

    return tax;
  };

  const calculateQualifiedDividendTax = (dividends, income, filingStatus) => {
    return calculateLongTermCapitalGainsTax(dividends, income, filingStatus);
  };

  const calculateYearlyProjection = () => {
    const { annualIncome, filingStatus, stateTaxRate } = inputs;
    
    const income = parseFloat(annualIncome);
    const stateRate = parseFloat(stateTaxRate) / 100;
    const brackets = taxBrackets[filingStatus];

    const projection = brackets.map(bracket => {
      const bracketWidth = bracket.max - bracket.min;
      const taxableInBracket = Math.max(0, Math.min(income, bracket.max) - bracket.min);
      const taxInBracket = taxableInBracket * bracket.rate;
      
      return {
        bracket: `${bracket.rate * 100}%`,
        range: `$${bracket.min.toLocaleString()} - $${bracket.max === Infinity ? '∞' : bracket.max.toLocaleString()}`,
        taxableAmount: taxableInBracket,
        federalTax: taxInBracket,
        stateTax: taxableInBracket * stateRate
      };
    }).filter(item => item.taxableAmount > 0);

    return projection;
  };

  const resetCalculator = () => {
    setInputs({
      buyPrice: "50.00",
      sellPrice: "65.00",
      shares: "100",
      purchaseDate: "2023-01-15",
      saleDate: "2024-01-15",
      filingStatus: "single",
      annualIncome: "75000",
      state: "CA",
      otherInvestmentIncome: "5000",
      taxYear: "2024",
      stateTaxRate: "9.3",
      applyNII: true,
      applyAMT: false,
      qualifiedDividends: "1000",
      nonQualifiedDividends: "500",
      washSaleAdjustment: "0",
      capitalLossCarryover: "0"
    });
    addNotification("Tax calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Gross Gain,${result.grossGain.toFixed(2)}`,
      `Net Gain,${result.netGain.toFixed(2)}`,
      `Holding Period,${result.holdingDays} days`,
      `Gain Type,${result.gainType}`,
      `Federal Tax,${result.federalTax.toFixed(2)}`,
      `State Tax,${result.stateTax.toFixed(2)}`,
      `Additional Taxes,${result.additionalTax.toFixed(2)}`,
      `Total Tax,${result.totalTax.toFixed(2)}`,
      `After-Tax Profit,${result.afterTaxProfit.toFixed(2)}`,
      `Effective Tax Rate,${result.effectiveTaxRate.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tax-estimation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Tax data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Tax calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Tax calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      shortTerm: { 
        purchaseDate: "2024-01-15", 
        saleDate: "2024-06-15",
        filingStatus: "single"
      },
      longTerm: { 
        purchaseDate: "2023-01-15", 
        saleDate: "2024-01-15",
        filingStatus: "married"
      },
      highIncome: {
        annualIncome: "250000",
        filingStatus: "single",
        applyNII: true
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} tax preset applied`, "info");
  };

  // Chart Data
  const taxBreakdownChartData = result ? {
    labels: ["Federal Tax", "State Tax", "Additional Taxes", "After-Tax Profit"],
    datasets: [{
      data: [
        result.totalFederalTax,
        result.stateTax,
        result.additionalTax,
        Math.max(result.afterTaxProfit, 0)
      ],
      backgroundColor: ["#DC2626", "#EA580C", "#D97706", "#16A34A"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const projectionData = calculateYearlyProjection();
  const taxBracketChartData = projectionData ? {
    labels: projectionData.map(d => d.bracket),
    datasets: [
      {
        label: 'Federal Tax',
        data: projectionData.map(d => d.federalTax),
        backgroundColor: "#DC2626",
        borderColor: "#DC2626",
        borderWidth: 1
      },
      {
        label: 'State Tax',
        data: projectionData.map(d => d.stateTax),
        backgroundColor: "#EA580C",
        borderColor: "#EA580C",
        borderWidth: 1
      }
    ]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Stock Tax Estimator</h1>
        </div>
        <p>Calculate capital gains taxes, estimate liabilities, and optimize your investment strategy</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Tax Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.result.afterTaxProfit >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.result.afterTaxProfit.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Tax Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "brackets" ? "active" : ""}`}
                onClick={() => setActiveTab("brackets")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Brackets' : 'Tax Brackets'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Quick Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('shortTerm')}>
                Short-Term
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('longTerm')}>
                Long-Term
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('highIncome')}>
                High Income
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Trade Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Buy Price</label>
                      <input 
                        type="number" 
                        name="buyPrice" 
                        value={inputs.buyPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Sell Price</label>
                      <input 
                        type="number" 
                        name="sellPrice" 
                        value={inputs.sellPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                      <input 
                        type="number" 
                        name="shares" 
                        value={inputs.shares} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Purchase Date</label>
                      <input 
                        type="date" 
                        name="purchaseDate" 
                        value={inputs.purchaseDate} 
                        onChange={handleChange}
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Sale Date</label>
                      <input 
                        type="date" 
                        name="saleDate" 
                        value={inputs.saleDate} 
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-user"></i>
                    Income Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-user"></i> Filing Status</label>
                      <select name="filingStatus" value={inputs.filingStatus} onChange={handleChange}>
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill"></i> Annual Income</label>
                      <input 
                        type="number" 
                        name="annualIncome" 
                        value={inputs.annualIncome} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> State Tax Rate (%)</label>
                      <input 
                        type="number" 
                        name="stateTaxRate" 
                        value={inputs.stateTaxRate} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Additional Income & Adjustments
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Qualified Dividends</label>
                      <input 
                        type="number" 
                        name="qualifiedDividends" 
                        value={inputs.qualifiedDividends} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Non-Qualified Dividends</label>
                      <input 
                        type="number" 
                        name="nonQualifiedDividends" 
                        value={inputs.nonQualifiedDividends} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-exchange-alt"></i> Wash Sale Adjustment</label>
                      <input 
                        type="number" 
                        name="washSaleAdjustment" 
                        value={inputs.washSaleAdjustment} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Other Investment Income</label>
                      <input 
                        type="number" 
                        name="otherInvestmentIncome" 
                        value={inputs.otherInvestmentIncome} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Capital Loss Carryover</label>
                      <input 
                        type="number" 
                        name="capitalLossCarryover" 
                        value={inputs.capitalLossCarryover} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-cog"></i>
                    Tax Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="applyNII" 
                          checked={inputs.applyNII} 
                          onChange={handleChange}
                        />
                        Apply Net Investment Income Tax (3.8%)
                      </label>
                    </div>
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="applyAMT" 
                          checked={inputs.applyAMT} 
                          onChange={handleChange}
                        />
                        Consider Alternative Minimum Tax
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Tax Brackets Tab */}
          {activeTab === "brackets" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Tax Brackets Overview
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-user"></i> Filing Status</label>
                    <select name="filingStatus" value={inputs.filingStatus} onChange={handleChange}>
                      <option value="single">Single</option>
                      <option value="married">Married Filing Jointly</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-money-bill"></i> Annual Income</label>
                    <input 
                      type="number" 
                      name="annualIncome" 
                      value={inputs.annualIncome} 
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> State Tax Rate (%)</label>
                    <input 
                      type="number" 
                      name="stateTaxRate" 
                      value={inputs.stateTaxRate} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {projectionData && (
                <div className="tax-brackets-list">
                  <h4>Federal Income Tax Breakdown</h4>
                  {projectionData.map((bracket, index) => (
                    <div key={index} className="bracket-item">
                      <div className="bracket-header">
                        <span className="bracket-rate">{bracket.bracket} Bracket</span>
                        <span className="bracket-range">{bracket.range}</span>
                      </div>
                      <div className="bracket-details">
                        <div className="bracket-detail">
                          <span>Taxable Amount:</span>
                          <span>${bracket.taxableAmount.toLocaleString()}</span>
                        </div>
                        <div className="bracket-detail">
                          <span>Federal Tax:</span>
                          <span>${bracket.federalTax.toFixed(2)}</span>
                        </div>
                        <div className="bracket-detail">
                          <span>State Tax:</span>
                          <span>${bracket.stateTax.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Tax Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No tax calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.result.afterTaxProfit >= 0 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.result.afterTaxProfit >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          ${calc.result.afterTaxProfit.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> {calc.result.gainType}</span>
                        <span><i className="fas fa-percentage"></i> Tax Rate: {calc.result.effectiveTaxRate.toFixed(2)}%</span>
                        <span><i className="fas fa-calendar"></i> {calc.result.holdingDays} days</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "brackets") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Tax Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className={`result-value ${result.afterTaxProfit >= 0 ? 'positive' : 'negative'}`}>
                          ${result.afterTaxProfit.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          After-Tax Profit
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.totalTax.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-file-invoice-dollar"></i>
                          Total Tax
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.effectiveTaxRate.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Effective Tax Rate
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.gainType}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-clock"></i>
                          Holding Period
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Tax Breakdown</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-flag-usa"></i> Federal Capital Gains Tax:</span>
                        <span>${result.federalTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-building"></i> State Tax:</span>
                        <span>${result.stateTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-plus-circle"></i> Additional Taxes:</span>
                        <span>${result.additionalTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Qualified Dividend Tax:</span>
                        <span>${result.qualifiedDividendTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Non-Qualified Dividend Tax:</span>
                        <span>${result.nonQualifiedDividendTax.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Trade Summary</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-up"></i> Gross Gain:</span>
                        <span>${result.grossGain.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-down"></i> Net Gain:</span>
                        <span>${result.netGain.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Holding Period:</span>
                        <span>{result.holdingDays} days</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-balance-scale"></i> Tax Efficiency:</span>
                        <span>{(100 - result.effectiveTaxRate).toFixed(1)}%</span>
                      </div>
                    </div>

                    {/* Tax Distribution Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Tax Distribution
                      </h3>
                      <Doughnut 
                        data={taxBreakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your trade details to see tax calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "brackets" && projectionData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Tax Bracket Analysis
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={taxBracketChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="tax-summary">
                  <h4>Total Tax Summary</h4><br/>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span>Total Federal Tax:</span>
                      <span>${projectionData.reduce((sum, item) => sum + item.federalTax, 0).toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Total State Tax:</span>
                      <span>${projectionData.reduce((sum, item) => sum + item.stateTax, 0).toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Combined Rate:</span>
                      <span>{((projectionData.reduce((sum, item) => sum + item.federalTax + item.stateTax, 0) / parseFloat(inputs.annualIncome)) * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default TaxEstimatorCalculator;